import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import {
  createPlaidLinkToken,
  createPlaidInstitutionReconnectLinkToken,
  createPlaidInstitutionUpdateLinkToken,
  exchangePlaidPublicToken,
  refreshPlaidInstitutionAccounts,
} from "../../../../../api/finances/plaidConnectivityAPI";
import InstitutionItem from "./InstitutionItem";
import GhostBar from "../../../loading/skeletons/GhostBar";
import FiInsightCard from "../../../../../pages/private/financial-insights/common/FiInsightCard";

function PlaidLinkLauncher({ linkToken, onSuccess, onExit }) {
  const [hasOpened, setHasOpened] = useState(false);
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  useEffect(() => {
    if (!linkToken || !ready || hasOpened) return;
    open();
    setHasOpened(true);
  }, [hasOpened, linkToken, open, ready]);

  return null;
}

function StandardPlaidInstitutionBody({
  institutions = [],
  isLoading = false,
  focusedInstitutionUuid,
  setFocusedInstitutionUuid,
  onInstitutionConnected,
  onInstitutionRemoved,
  onSetError,
  copy,
  connectActionTrigger,
  setIsConnectDisabled,
}) {
  const [plaidSession, setPlaidSession] = useState(null);
  const [isCreatingLinkToken, setIsCreatingLinkToken] = useState(false);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [updatingInstitutionUuid, setUpdatingInstitutionUuid] = useState(null);

  // Sync disable status to header button
  useEffect(() => {
    const disabled =
      isCreatingLinkToken || isExchangingToken || Boolean(plaidSession);
    setIsConnectDisabled(disabled);
  }, [
    isCreatingLinkToken,
    isExchangingToken,
    plaidSession,
    setIsConnectDisabled,
  ]);

  const handleConnectClick = useCallback(async () => {
    setIsCreatingLinkToken(true);
    onSetError("");
    setPlaidSession(null);

    try {
      const { link_token: newLinkToken } = await createPlaidLinkToken();
      setPlaidSession({ linkToken: newLinkToken, mode: "connect" });
    } catch (error) {
      onSetError(copy?.start_link_error);
    } finally {
      setIsCreatingLinkToken(false);
    }
  }, [copy?.start_link_error, onSetError]);

  // Listen to header button click trigger
  useEffect(() => {
    if (connectActionTrigger > 0) {
      handleConnectClick();
    }
  }, [connectActionTrigger, handleConnectClick]);

  const handlePlaidSuccess = useCallback(
    async (publicToken) => {
      setIsExchangingToken(true);
      onSetError("");

      try {
        if (plaidSession?.mode === "update") {
          const result = await refreshPlaidInstitutionAccounts(
            plaidSession.institutionUuid,
          );
          setFocusedInstitutionUuid(plaidSession.institutionUuid);
          onInstitutionConnected?.(result);
          return;
        }

        const result = await exchangePlaidPublicToken(publicToken);
        setFocusedInstitutionUuid(result?.institution?.uuid || null);
        onInstitutionConnected?.(result);
      } catch (error) {
        onSetError(copy?.connection_error || "Failed to connect institution.");
      } finally {
        setIsExchangingToken(false);
        setPlaidSession(null);
        setUpdatingInstitutionUuid(null);
      }
    },
    [
      copy?.connection_error,
      onInstitutionConnected,
      onSetError,
      plaidSession,
      setFocusedInstitutionUuid,
    ],
  );

  const handlePlaidExit = useCallback(
    (error) => {
      if (error) onSetError(copy?.link_closed_error);
      setPlaidSession(null);
      setUpdatingInstitutionUuid(null);
    },
    [copy?.link_closed_error, onSetError],
  );

  const handleAddAccounts = async (institution) => {
    const institutionUuid = institution?.uuid;
    if (!institutionUuid) {
      onSetError(copy?.missing_uuid_error);
      return;
    }

    setUpdatingInstitutionUuid(institutionUuid);
    setFocusedInstitutionUuid(institutionUuid);
    onSetError("");
    setPlaidSession(null);

    try {
      const isDisconnected = institution?.is_active === false;
      const { link_token: newLinkToken } = isDisconnected
        ? await createPlaidInstitutionReconnectLinkToken(institutionUuid)
        : await createPlaidInstitutionUpdateLinkToken(institutionUuid);
      setPlaidSession({
        linkToken: newLinkToken,
        mode: isDisconnected ? "reconnect" : "update",
        institutionUuid,
      });
    } catch (error) {
      onSetError(copy?.start_institution_link_error);
      setUpdatingInstitutionUuid(null);
    }
  };

  return (
    <>
      {plaidSession?.linkToken && (
        <PlaidLinkLauncher
          key={`${plaidSession.mode}-${plaidSession.linkToken}`}
          linkToken={plaidSession.linkToken}
          onSuccess={handlePlaidSuccess}
          onExit={handlePlaidExit}
        />
      )}

      {isLoading ? (
        <GhostBar />
      ) : (
        institutions.map((inst, index) => (
          <InstitutionItem
            key={inst.uuid || index}
            institution={inst}
            defaultOpen={
              focusedInstitutionUuid
                ? inst.uuid === focusedInstitutionUuid
                : index === 0
            }
            onInstitutionRemoved={onInstitutionRemoved}
            onInstitutionUpdated={(updatedInstitution) =>
              onInstitutionConnected?.({ institution: updatedInstitution })
            }
            onAddAccounts={handleAddAccounts}
            isAddingAccounts={updatingInstitutionUuid === inst.uuid}
          />
        ))
      )}

      {!isLoading && institutions.length === 0 && (
        <FiInsightCard title={copy?.empty_title}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginTop: "8px",
            }}
          >
            <p
              className="reset-margin"
              style={{
                color: "#6b7280",
                fontSize: "14px",
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {copy?.empty_description}
            </p>
          </div>
        </FiInsightCard>
      )}
    </>
  );
}

export default StandardPlaidInstitutionBody;
